'use client'
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react'
import { Display, OrderStatus, Top } from '../../../utils/type'
import { formatSat, NoOperation, parseSat } from '../../../utils'
import { OrderItem, Sort } from '../../../utils/http/Services/project'
import useWindowInfiniteScroll from '../../../hooks/useWindowInfiniteScroll'
import { I, isNoMore } from '../../../utils/http/infinite'
import { Services } from '../../../utils/http/Services'
import { useParams } from 'next/navigation'

export enum Mode {
  Items,
  Activity,
}

export const FilterContext = createContext<{
  top: Top
  priceMax?: string
  setPriceMax: (max?: string) => void
  priceMin?: string
  setPriceMin: (min?: string) => void
  showAll: boolean
  setShowAll: (showAll: boolean) => void
  onTopChange: (val: Top) => void
  display: Display
  onDisplayChange: (val: Display) => void
  orders: OrderItem[]
  isLoading: boolean
  sort: Sort
  setSort: Dispatch<SetStateAction<Sort>>
  includePending: boolean
  setIncludePending: Dispatch<SetStateAction<boolean>>
  mode: Mode
  setMode: Dispatch<SetStateAction<Mode>>
  onBuyed: (inscriptionId: string) => void
}>({
  top: Top.Top_1,
  onTopChange: NoOperation,
  display: Display.GRID,
  onDisplayChange: NoOperation,
  orders: [],
  isLoading: false,
  sort: Sort.PriceAsc,
  setSort: () => Sort.PriceDesc,
  includePending: false,
  setIncludePending: () => false,
  priceMin: undefined,
  priceMax: undefined,
  setPriceMax: NoOperation,
  setPriceMin: NoOperation,
  showAll: false,
  setShowAll: NoOperation,
  mode: Mode.Items,
  setMode: NoOperation,
  onBuyed: NoOperation,
})

export function FilterContextProvider(props: { children: ReactNode }) {
  const params = useParams()
  const [top, setTop] = useState<Top>(Top.ALL)
  const [display, setDisplay] = useState<Display>(Display.GRID)
  const [sort, setSort] = useState<Sort>(Sort.PriceAsc)
  const [includePending, setIncludePending] = useState(false)
  const [priceMin, setPriceMin] = useState<string | undefined>(undefined)
  const [priceMax, setPriceMax] = useState<string | undefined>(undefined)
  const [showAll, setShowAll] = useState(true)
  const _priceMin = +(priceMin || 0) > 0 ? +(priceMin || 0) : undefined
  const _priceMax = +(priceMax || 0) > 0 ? +(priceMax || 0) : undefined
  const [mode, setMode] = useState<Mode>(Mode.Items)
  const { data, loadingMore, loading, mutate } = useWindowInfiniteScroll(
    I(Services.projectService.orderList, {
      limit: 20,
      top,
      collection_id: +params.id,
      sort,
      show_pending: includePending,
      show_all: showAll,
      price_min: parseSat(_priceMin || 0),
      price_max: parseSat(_priceMax || 0),
    }),
    {
      reloadDeps: [top, sort, includePending, showAll, _priceMin, _priceMax],
      isNoMore,
      debounceWait: 500,
    }
  )
  const orders = data?.list || []
  const isLoading = loading || loadingMore
  const onBuyed = (inscriptionId: string) => {
    mutate((data) => {
      const target = data?.list.find((item) => item.inscription_id === inscriptionId)
      if (target) {
        target.order_status = OrderStatus.Pending
        return Object.assign({}, data)
      }
      return data
    })
  }
  return (
    <FilterContext.Provider
      value={{
        top,
        display,
        onTopChange: setTop,
        onDisplayChange: setDisplay,
        orders,
        isLoading,
        sort,
        setSort,
        includePending,
        setIncludePending,
        priceMax,
        setPriceMax,
        priceMin,
        setPriceMin,
        showAll,
        setShowAll,
        mode,
        setMode,
        onBuyed,
      }}
    >
      {props.children}
    </FilterContext.Provider>
  )
}

export function useFilterContext() {
  return useContext(FilterContext)
}
